import { useEffect, useCallback, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useVisit } from '@zenbusiness/application-commons-visit';
import Box from '@zenbusiness/zui/Box';
import Container from '@zenbusiness/zui/Container';
import SharedLottieAsset from '@zenbusiness/zui/SharedLottieAsset';

import Transition from '#client/components/Transition';
import { STANDALONE_THREAD_STATUS } from '#client/components/utils/constants';
import { getWarmStartConfig } from '#client/constants/warmStartMessages';
import { useInstructionRenderer } from '#client/hooks/useInstructionRenderer';
import { useVisitorThreadCreation } from '#client/hooks/useVisitorThreadCreation';

import DynamicRenderer from '../components/DynamicRenderer';
import LegalFooter from '../components/LegalFooter';
import StandaloneHeader from '../components/StandaloneHeader';

const PERSIST_KEY = 'business-name-generation';

const FORM_PERSIST_KEY = `${PERSIST_KEY}_form`;
const FLOW_PERSIST_KEY = `${PERSIST_KEY}_flow`;

const userSelectionToString = (obj) => {
  return Object.entries(obj)
    .map(([key, value]) => {
      if (value instanceof Object) {
        if (value.selectedId === 'other') {
          return `${key}: ${value.inputValue}`;
        }
      }

      return `${key}: ${value?.value || value}`;
    })
    .join('. ');
};

const BASE_PROMPT = 'generate more business names based on these options: ';

const LOADER_ICON = (
  <SharedLottieAsset height="20rem" src="/s2/animation/lottie/velo-avatar-loading-light.json" />
);

// Helper functions for thread state management
const getStoredThreadState = () => {
  try {
    const stored = localStorage.getItem(PERSIST_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('[BusinessNameVisitor] Failed to parse thread state from localStorage:', error);
    return null;
  }
};

const setStoredThreadState = (threadUuid, status) => {
  try {
    localStorage.setItem(PERSIST_KEY, JSON.stringify({ threadUuid, status }));
  } catch (error) {
    console.warn('[BusinessNameVisitor] Failed to store thread state in localStorage:', error);
  }
};

export default function BusinessNameVisitor() {
  const { visitUuid, visitProcessed } = useVisit();
  const navigate = useNavigate();
  const { value: enableBusinessNameGeneration, loading: enableBusinessNameGenerationLoading } =
    useExperimentFlag('enable-business-name-generation', false, true);

  // Redirect to home if enable Business Name Generation is false
  useEffect(() => {
    if (!enableBusinessNameGenerationLoading && !enableBusinessNameGeneration) {
      window.location.replace('/');
    }
  }, [enableBusinessNameGeneration, enableBusinessNameGenerationLoading]);

  const {
    loading: threadLoading,
    error: threadError,
    createNewThread,
    retryCount
  } = useVisitorThreadCreation();

  // Use the existing instruction renderer with the threadUuid from URL
  const {
    error: instructionError,
    instructions,
    isError: isInstructionError,
    loadingNext,
    loadNextInstruction,
    loadExistingMessages,
    resetStateForNewThread,
    setInstructions
  } = useInstructionRenderer();

  const getLastUrlSegment = useCallback(() => {
    const pathSegments = window.location.pathname.split('/');
    return pathSegments[pathSegments.length - 1];
  }, []);

  const createBusinessNameThread = useCallback(
    async (visitUuidParam) => {
      // Check for existing in-progress thread
      const storedThreadState = getStoredThreadState();
      if (
        storedThreadState &&
        storedThreadState.status === STANDALONE_THREAD_STATUS.in_progress &&
        storedThreadState.threadUuid
      ) {
        // Resume from existing thread instead of creating a new one
        resetStateForNewThread(storedThreadState.threadUuid);
        loadExistingMessages(
          { visitUuid: visitUuidParam },
          storedThreadState.threadUuid,
          'standalone'
        );
        // Note: Instructions will be loaded by the instruction renderer
        return storedThreadState.threadUuid;
      }

      // Create thread with warm start input if available
      const initialInput = getWarmStartConfig(getLastUrlSegment());
      const businessNameInput = {
        visitUuid: visitUuidParam,
        ...initialInput
      };
      const { threadUuid: newThreadUuid, data } = await createNewThread(
        visitUuidParam,
        businessNameInput,
        { redirect: false }
      );

      // Store thread state properly in localStorage
      resetStateForNewThread(newThreadUuid);
      setStoredThreadState(newThreadUuid, STANDALONE_THREAD_STATUS.in_progress);
      setInstructions(data);
      return newThreadUuid;
    },
    [
      createNewThread,
      getLastUrlSegment,
      resetStateForNewThread,
      setInstructions,
      loadExistingMessages
    ]
  );

  // Add ref to track if thread creation has been initiated to prevent double execution
  const threadCreationInitiated = useRef(false);

  // Reset thread creation flag when visitUuid changes
  useEffect(() => {
    threadCreationInitiated.current = false;
  }, [visitUuid]);

  // Remove thread state if it is not in progress on mount
  useEffect(() => {
    const storedThreadState = getStoredThreadState();
    if (
      storedThreadState &&
      storedThreadState.status !== STANDALONE_THREAD_STATUS.in_progress &&
      !threadCreationInitiated.current
    ) {
      localStorage.removeItem(PERSIST_KEY);
      localStorage.removeItem(FLOW_PERSIST_KEY);
      localStorage.removeItem(FORM_PERSIST_KEY);
    }
  }, []);

  // Auto-create thread on mount (after LD flag loading is complete)
  useEffect(() => {
    if (enableBusinessNameGeneration) {
      // Since we have a cronjob that removes inactive threads, we need to check if the thread is still valid
      const threadNotFound = isInstructionError && instructionError?.errorType === 'INVALID_THREAD';
      if (threadNotFound) {
        threadCreationInitiated.current = false;
        localStorage.removeItem(PERSIST_KEY);
        localStorage.removeItem(FLOW_PERSIST_KEY);
        localStorage.removeItem(FORM_PERSIST_KEY);
      }

      // Prevent double execution by checking if thread creation has already been initiated
      if (!threadLoading && !threadError && visitUuid && !threadCreationInitiated.current) {
        createBusinessNameThread(visitUuid).then((newThreadUuid) => {
          if (newThreadUuid) {
            threadCreationInitiated.current = true;
          }
        });
      }
    }
  }, [
    createBusinessNameThread,
    enableBusinessNameGenerationLoading,
    threadLoading,
    threadError,
    visitUuid,
    isInstructionError,
    instructionError?.errorType,
    enableBusinessNameGeneration
  ]);

  const clearStoredThreadState = useCallback(() => {
    localStorage.removeItem(PERSIST_KEY);
  }, []);

  const handleComponentClick = useCallback(
    async (action) => {
      const userProfile = action?.payload?.answers || {};

      // Update form state when choice flow is submitted
      if (action.type === 'choice_flow_submission') {
        await loadNextInstruction(userSelectionToString(userProfile), 'invoke', 'standalone', {
          visitUuid,
          agentName: 'business-name-generation'
        });
        const storedThreadState = getStoredThreadState();
        setStoredThreadState(storedThreadState.threadUuid, STANDALONE_THREAD_STATUS.complete);
      }

      if (action.type === 'button_click') {
        await loadNextInstruction(
          `${BASE_PROMPT} ${userSelectionToString(userProfile)}`,
          'invoke',
          'standalone',
          {
            visitUuid,
            agentName: 'business-name-generation'
          }
        );
      }

      if (action.type === 'business_name_results_start_over') {
        localStorage.removeItem(PERSIST_KEY);
        localStorage.removeItem(FLOW_PERSIST_KEY);
        localStorage.removeItem(FORM_PERSIST_KEY);
        // Clear thread state to allow creating a new thread
        clearStoredThreadState();
        // refresh the page
        navigate(0);
      }
    },
    [clearStoredThreadState, loadNextInstruction, navigate, visitUuid]
  );

  if (!visitProcessed) {
    return <Transition icon={LOADER_ICON} type="VISIT_LOADING" />;
  }

  // Show thread creation loading state
  if (threadLoading) {
    return (
      <Transition
        icon={LOADER_ICON}
        type={retryCount > 0 ? 'THREAD_CREATING_RETRY' : 'THREAD_CREATING'}
        typeProps={{ retryCount }}
      />
    );
  }

  // Show scanning animation while loading next instruction
  if (loadingNext && threadCreationInitiated.current) {
    return <Transition icon={LOADER_ICON} type="BUSINESS_NAME_GENERATING" />;
  }

  if ((loadingNext && !threadCreationInitiated.current) || enableBusinessNameGenerationLoading) {
    return <Transition icon={LOADER_ICON} type="BUSINESS_NAME_LOADING" />;
  }

  // Note: CustomFormProvider is now at app root (Providers.js)
  // ChoiceFlow registers its persistKey dynamically when it mounts
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <StandaloneHeader
        subtitle="Discover the perfect name for your business with AI-powered suggestions. Complete with domain availability and trademark searches."
        title="Business Name Generator"
      />
      <Container>
        <DynamicRenderer
          currentMode="initial"
          fullWidth
          instructions={instructions}
          isChatMode={false}
          onComponentClick={handleComponentClick}
        />
        <Box
          display="flex"
          justifyContent="center"
          sx={(theme) => ({
            width: '100%',
            margin: 'auto',
            paddingBlock: theme.spacing(1.5)
          })}
        >
          <LegalFooter />
        </Box>
      </Container>
    </Box>
  );
}
